<script setup>
import { reactive, computed, watch, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useMapGetter } from 'dashboard/composables/store';
import { useCampaignTemplates } from 'dashboard/composables/useCampaignTemplates';
import CampaignsAPI from 'dashboard/api/campaigns';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';

const props = defineProps({
  whatsappTemplateCategory: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(['submit', 'cancel']);

const { t } = useI18n();

const formState = {
  uiFlags: useMapGetter('campaigns/getUIFlags'),
  labels: useMapGetter('labels/getLabels'),
  inboxes: useMapGetter('inboxes/getWhatsAppInboxes'),
};

const state = reactive({
  title: '',
  inboxId: null,
  templateIdA: null,
  templateIdB: null,
  scheduledAt: null,
  selectedAudience: [],
});

const templateParserRefA = ref(null);
const templateParserRefB = ref(null);

const inboxIdRef = computed(() => state.inboxId);
const fixedTemplateCategory = computed(() => {
  const category = props.whatsappTemplateCategory?.toLowerCase();
  return ['utility', 'marketing'].includes(category) ? category : null;
});
const {
  templateOptions,
  marketingOnly,
  getSelectedTemplate,
  toggleMarketingOnly,
} = useCampaignTemplates(inboxIdRef, { categoryRef: fixedTemplateCategory });

const selectedTemplateA = computed(() =>
  getSelectedTemplate(state.templateIdA)
);
const selectedTemplateB = computed(() =>
  getSelectedTemplate(state.templateIdB)
);

const rules = {
  title: { required, minLength: minLength(1) },
  inboxId: { required },
  templateIdA: { required },
  templateIdB: { required },
  scheduledAt: { required },
  selectedAudience: { required },
};

const v$ = useVuelidate(rules, state);

const isCreating = computed(() => formState.uiFlags.value.isCreating);

const currentDateTime = computed(() => {
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
});

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({
    value: item[valueKey],
    label: item[labelKey],
  })) ?? [];

const audienceList = computed(() =>
  mapToOptions(formState.labels.value, 'id', 'title')
);

const inboxOptions = computed(() =>
  mapToOptions(formState.inboxes.value, 'id', 'name')
);

const getErrorMessage = (field, errorKey) => {
  const baseKey = 'CAMPAIGN.WHATSAPP.AB_TEST.FORM';
  return v$.value[field].$error ? t(`${baseKey}.${errorKey}.ERROR`) : '';
};

const formErrors = computed(() => ({
  title: getErrorMessage('title', 'TITLE'),
  inbox: getErrorMessage('inboxId', 'INBOX'),
  templateA: getErrorMessage('templateIdA', 'TEMPLATE_A'),
  templateB: getErrorMessage('templateIdB', 'TEMPLATE_B'),
  scheduledAt: getErrorMessage('scheduledAt', 'SCHEDULED_AT'),
  audience: getErrorMessage('selectedAudience', 'AUDIENCE'),
}));

const hasRequiredTemplateParamsA = computed(() => {
  if (!state.templateIdA || !templateParserRefA.value) return false;
  return !templateParserRefA.value.v$.$invalid;
});

const hasRequiredTemplateParamsB = computed(() => {
  if (!state.templateIdB || !templateParserRefB.value) return false;
  return !templateParserRefB.value.v$.$invalid;
});

const isSubmitDisabled = computed(
  () =>
    v$.value.$invalid ||
    !hasRequiredTemplateParamsA.value ||
    !hasRequiredTemplateParamsB.value
);

const formatToUTCString = localDateTime =>
  localDateTime ? new Date(localDateTime).toISOString() : null;

const handleCancel = () => emit('cancel');

const buildTemplateParams = (template, parserRef) => {
  const parserData = parserRef;
  return {
    name: template?.name || '',
    namespace: template?.namespace || '',
    category: template?.category || 'UTILITY',
    status: template?.status || '',
    language: template?.language || 'en_US',
    processed_params: parserData?.processedParams || {},
  };
};

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  const templateA = selectedTemplateA.value;
  const templateB = selectedTemplateB.value;
  const contentA =
    templateParserRefA.value?.bodyText ||
    templateParserRefA.value?.renderedTemplate ||
    '';
  const contentB =
    templateParserRefB.value?.bodyText ||
    templateParserRefB.value?.renderedTemplate ||
    '';

  emit('submit', {
    title: state.title,
    message: contentA,
    template_params: buildTemplateParams(templateA, templateParserRefA.value),
    inbox_id: state.inboxId,
    whatsapp_template_category:
      fixedTemplateCategory.value || templateA?.category?.toLowerCase(),
    scheduled_at: formatToUTCString(state.scheduledAt),
    audience: state.selectedAudience?.map(id => ({ id, type: 'Label' })),
    variant_b: {
      message: contentB,
      template_params: buildTemplateParams(templateB, templateParserRefB.value),
    },
  });
};

// Reset template selection when inbox changes
watch(
  () => state.inboxId,
  () => {
    state.templateIdA = null;
    state.templateIdB = null;
  }
);

// Audience estimation
const audienceEstimate = ref(null);
const isFetchingEstimate = ref(false);

const fetchAudienceEstimate = async labelIds => {
  if (!labelIds.length) {
    audienceEstimate.value = null;
    return;
  }
  isFetchingEstimate.value = true;
  try {
    const response = await CampaignsAPI.audienceEstimate(labelIds);
    audienceEstimate.value = response.data;
  } catch {
    audienceEstimate.value = null;
  } finally {
    isFetchingEstimate.value = false;
  }
};

watch(
  () => [...state.selectedAudience],
  newVal => fetchAudienceEstimate(newVal)
);
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <Input
      v-model="state.title"
      :label="t('CAMPAIGN.WHATSAPP.AB_TEST.FORM.TITLE.LABEL')"
      :placeholder="t('CAMPAIGN.WHATSAPP.AB_TEST.FORM.TITLE.PLACEHOLDER')"
      :message="formErrors.title"
      :message-type="formErrors.title ? 'error' : 'info'"
    />

    <div class="flex flex-col gap-1">
      <label for="inbox" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.AB_TEST.FORM.INBOX.LABEL') }}
      </label>
      <ComboBox
        id="inbox"
        v-model="state.inboxId"
        :options="inboxOptions"
        :has-error="!!formErrors.inbox"
        :placeholder="t('CAMPAIGN.WHATSAPP.AB_TEST.FORM.INBOX.PLACEHOLDER')"
        :message="formErrors.inbox"
        class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
      />
    </div>

    <div v-if="!fixedTemplateCategory" class="flex items-center justify-end">
      <button
        type="button"
        class="text-xs text-n-slate-11 hover:text-n-slate-12"
        @click="toggleMarketingOnly"
      >
        {{
          marketingOnly
            ? t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.SHOW_ALL')
            : t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.MARKETING_ONLY')
        }}
      </button>
    </div>

    <!-- Variant A -->
    <fieldset
      class="flex flex-col gap-2 p-3 rounded-lg border border-n-blue-6 bg-n-blue-2"
    >
      <legend class="px-2 text-xs font-semibold text-n-blue-11">
        {{ t('CAMPAIGN.WHATSAPP.AB_TEST.FORM.VARIANT_A') }}
      </legend>
      <div class="flex flex-col gap-1">
        <ComboBox
          v-model="state.templateIdA"
          :options="templateOptions"
          :has-error="!!formErrors.templateA"
          :placeholder="
            t('CAMPAIGN.WHATSAPP.AB_TEST.FORM.TEMPLATE_A.PLACEHOLDER')
          "
          :message="formErrors.templateA"
          class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
        />
      </div>
      <WhatsAppTemplateParser
        v-if="selectedTemplateA"
        ref="templateParserRefA"
        :template="selectedTemplateA"
      />
    </fieldset>

    <!-- Variant B -->
    <fieldset
      class="flex flex-col gap-2 p-3 rounded-lg border border-n-violet-6 bg-n-violet-2"
    >
      <legend class="px-2 text-xs font-semibold text-n-violet-11">
        {{ t('CAMPAIGN.WHATSAPP.AB_TEST.FORM.VARIANT_B') }}
      </legend>
      <div class="flex flex-col gap-1">
        <ComboBox
          v-model="state.templateIdB"
          :options="templateOptions"
          :has-error="!!formErrors.templateB"
          :placeholder="
            t('CAMPAIGN.WHATSAPP.AB_TEST.FORM.TEMPLATE_B.PLACEHOLDER')
          "
          :message="formErrors.templateB"
          class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
        />
      </div>
      <WhatsAppTemplateParser
        v-if="selectedTemplateB"
        ref="templateParserRefB"
        :template="selectedTemplateB"
      />
    </fieldset>

    <div class="flex flex-col gap-1">
      <label for="audience" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.AB_TEST.FORM.AUDIENCE.LABEL') }}
      </label>
      <TagMultiSelectComboBox
        v-model="state.selectedAudience"
        :options="audienceList"
        :label="t('CAMPAIGN.WHATSAPP.AB_TEST.FORM.AUDIENCE.LABEL')"
        :placeholder="t('CAMPAIGN.WHATSAPP.AB_TEST.FORM.AUDIENCE.PLACEHOLDER')"
        :has-error="!!formErrors.audience"
        :message="formErrors.audience"
        class="[&>div>button]:bg-n-alpha-black2"
      />
      <div
        v-if="audienceEstimate"
        class="flex items-center gap-2 px-3 py-2 text-xs rounded-lg bg-n-alpha-1 border border-n-weak"
      >
        <span class="i-lucide-users text-n-slate-11" />
        <span class="text-n-slate-11">
          {{
            t('CAMPAIGN.WHATSAPP.AB_TEST.FORM.AUDIENCE.ESTIMATE', {
              total: audienceEstimate.total,
              withPhone: audienceEstimate.with_phone,
              perVariant: Math.ceil(audienceEstimate.with_phone / 2),
            })
          }}
        </span>
      </div>
    </div>

    <div class="flex flex-col gap-1">
      <label class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.AB_TEST.FORM.SCHEDULED_AT.LABEL') }}
      </label>
      <Input
        v-model="state.scheduledAt"
        type="datetime-local"
        :min="currentDateTime"
        :placeholder="
          t('CAMPAIGN.WHATSAPP.AB_TEST.FORM.SCHEDULED_AT.PLACEHOLDER')
        "
        :message="formErrors.scheduledAt"
        :message-type="formErrors.scheduledAt ? 'error' : 'info'"
      />
    </div>

    <div class="flex gap-3 justify-between items-center w-full">
      <Button
        variant="faded"
        color="slate"
        type="button"
        :label="t('CAMPAIGN.WHATSAPP.AB_TEST.FORM.BUTTONS.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        :label="t('CAMPAIGN.WHATSAPP.AB_TEST.FORM.BUTTONS.CREATE')"
        class="w-full"
        type="submit"
        :is-loading="isCreating"
        :disabled="isCreating || isSubmitDisabled"
      />
    </div>
  </form>
</template>
